import { useMemo, useRef, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Pagination } from "antd";
import {
  Calendar,
  Copy,
  Download,
  Edit2,
  Eye,
  Info,
  Link2,
  Paperclip,
  Trash,
  X,
} from "lucide-react";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { getEmployeeId } from "@/lib/storeManager";
import {
  PtsPurchaseApprovalService,
  PtsProjectService,
  type PurchaseApprovalAttachment,
  type PurchaseApprovalItem,
  type PurchaseApprovalPriority,
  type PurchaseApprovalCurrency,
} from "@/services/APIs";
import { isPTSAdmin } from "../../utils/rolePermissions";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  PurchaseApprovalStatusBadge,
  PurchaseApprovalPurchaseStatusBadge,
} from "./StatusBadge";
import ReasonCell from "./ReasonCell";
import PurchaseApprovalViewModal from "./PurchaseApprovalViewModal";
import PurchaseApprovalFilters from "./PurchaseApprovalFilters";
import {
  EMPTY_PURCHASE_APPROVAL_FILTERS,
  type PurchaseApprovalFilterValues,
} from "./PurchaseApprovalFilters.constants";
import {
  PURCHASE_APPROVAL_PRIORITIES,
  PURCHASE_APPROVAL_CURRENCY_OPTIONS,
  DEFAULT_PURCHASE_CURRENCY,
  currencySymbol,
  filterPurchaseApprovals,
  formatDateYmd,
  formatMoney,
  formatPurchaseApprovalReason,
  normalizeStatus,
  resolveName,
} from "./utils";
import { formatDateDmy } from "../LeaveRequests/utils";

type EmployeeOption = { value: string; label: string };

type FormState = {
  project: string;
  title: string;
  priority: PurchaseApprovalPriority;
  currency: PurchaseApprovalCurrency;
  requested_date: string;
  expected_date: string;
  cost: string;
  frequency: string;
  requested_by: string;
  description: string;
  attachments_link: string;
};

const emptyForm = (): FormState => ({
  project: "",
  title: "",
  priority: "Medium",
  currency: DEFAULT_PURCHASE_CURRENCY,
  requested_date: "",
  expected_date: "",
  cost: "",
  frequency: "One-time",
  requested_by: getEmployeeId() ?? "",
  description: "",
  attachments_link: "",
});

function toDateInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }
  const ymd = formatDateYmd(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : "";
}

type BusinessAnalystTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
  employeeOptions: EmployeeOption[];
};

const BusinessAnalystTab = ({
  projectNameById,
  employeeNameById,
  employeeOptions,
}: BusinessAnalystTabProps) => {
  const queryClient = useQueryClient();
  const formCardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    PurchaseApprovalAttachment[]
  >([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [filterValues, setFilterValues] =
    useState<PurchaseApprovalFilterValues>(EMPTY_PURCHASE_APPROVAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues]);

  const isEditMode = !!editingId;

  const currentEmployeeId = getEmployeeId() ?? "";
  const canViewAllEmployeeRequests = isPTSAdmin();
  const requestedByFilter = canViewAllEmployeeRequests
    ? undefined
    : currentEmployeeId || undefined;

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["purchase-approval-open-projects"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "purchase-approvals",
      "ba-list",
      canViewAllEmployeeRequests ? "all" : currentEmployeeId,
      filterValues.projectId,
      filterValues.status,
      filterValues.purchaseStatus,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsPurchaseApprovalService.getPurchaseApprovals({
        page: currentPage,
        page_size: pageSize,
        project: filterValues.projectId,
        status: filterValues.status,
        purchase_status: filterValues.purchaseStatus,
        requested_by: requestedByFilter,
      }),
    enabled: canViewAllEmployeeRequests || !!currentEmployeeId,
  });

  const recentRequests = data?.results;
  const totalCount = data?.count ?? 0;

  const filteredRequests = useMemo(
    () =>
      filterPurchaseApprovals(recentRequests ?? [], {
        projectId: filterValues.projectId,
        status: filterValues.status,
        purchaseStatus: filterValues.purchaseStatus,
      }),
    [recentRequests, filterValues]
  );

  const handleExportCSV = async () => {
    try {
      const res = await PtsPurchaseApprovalService.getPurchaseApprovals({
        page_size: 100000,
        project: filterValues.projectId,
        status: filterValues.status,
        purchase_status: filterValues.purchaseStatus,
        requested_by: requestedByFilter,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "ID",
        "PR Number",
        "Project ID",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Date",
        "Cost",
        "Currency",
        "Frequency",
        "Requested By ID",
        "Requested By Name",
        "Description",
        "Status",
        "Purchase Status",
        "Reason",
        "Attachments Link",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const reqName = resolveName(item.requested_by, employeeNameById);

        return [
          escapeCsv(item.id),
          escapeCsv(item.pr_number),
          escapeCsv(item.project),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateDmy(item.requested_date)),
          escapeCsv(formatDateDmy(item.expected_date)),
          escapeCsv(item.cost),
          escapeCsv(item.currency ?? "INR"),
          escapeCsv(item.frequency),
          escapeCsv(item.requested_by),
          escapeCsv(reqName),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(item.purchase_status),
          escapeCsv(formatPurchaseApprovalReason(item)),
          escapeCsv(item.attachments_link),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `purchase-approvals-ba-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setAttachments([]);
    setExistingAttachments([]);
    setEditingId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const saveMutation = useMutation<
    PurchaseApprovalItem | undefined,
    Error,
    { keepForm?: boolean } | undefined
  >({
    mutationFn: async (_variables: { keepForm?: boolean } | undefined) => {
      const payload = {
        ...form,
        attachments,
      };
      if (editingId) {
        return PtsPurchaseApprovalService.updatePurchaseApprovalForm(
          editingId,
          payload
        );
      }
      return PtsPurchaseApprovalService.createPurchaseApproval(payload);
    },
    onSuccess: (result, variables) => {
      const keepForm = variables?.keepForm ?? false;
      if (!result?.id) {
        return;
      }
      notify.success(
        keepForm
          ? "Purchase request copied and saved successfully"
          : isEditMode
            ? "Purchase request updated successfully"
            : "Purchase request submitted for approval"
      );
      if (keepForm) {
        setEditingId(null);
        setExistingAttachments([]);
        setAttachments([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        resetForm();
      }
      queryClient.invalidateQueries({ queryKey: ["purchase-approvals"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      PtsPurchaseApprovalService.deletePurchaseApproval(id),
    onSuccess: (_result, id) => {
      notify.success("Purchase request deleted successfully");
      if (editingId === id) {
        resetForm();
      }
      queryClient.invalidateQueries({ queryKey: ["purchase-approvals"] });
    },
  });

  const onFilesSelected = (files: FileList | null) => {
    if (!files?.length) {
      return;
    }
    setAttachments(prev => [...prev, ...Array.from(files)]);
  };

  const validate = (): string | null => {
    if (!form.project) {
      return "Please select a project";
    }
    if (!form.title.trim()) {
      return "Please enter a title";
    }
    if (!form.priority) {
      return "Please select a priority";
    }
    if (!form.currency) {
      return "Please select a currency";
    }
    if (!form.requested_date) {
      return "Please select a requested date";
    }
    if (!form.expected_date) {
      return "Please select an expected date";
    }
    // if (!form.cost || Number(form.cost) <= 0) {
    //   return "Please enter a valid cost";
    // }
    if (!form.frequency) {
      return "Please select a frequency";
    }
    if (!form.requested_by) {
      return "Please select who requested this purchase";
    }
    if (!form.description.trim()) {
      return "Please enter a description";
    }
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate({ keepForm: false });
  };

  const handleCopyToCreate = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate({ keepForm: true });
  };

  const handleEdit = (item: PurchaseApprovalItem) => {
    const priority = (["High", "Medium", "Low"] as const).includes(
      item.priority as PurchaseApprovalPriority
    )
      ? (item.priority as PurchaseApprovalPriority)
      : "Medium";

    const currencyValue = String(item.currency ?? "")
      .trim()
      .toUpperCase();
    const currency: PurchaseApprovalCurrency =
      currencyValue === "US" || currencyValue === "USD" ? "US" : "INR";

    setEditingId(item.id);
    setForm({
      project: String(item.project || ""),
      title: item.title || "",
      priority,
      currency,
      requested_date: toDateInputValue(item.requested_date),
      expected_date: toDateInputValue(item.expected_date),
      cost:
        item.cost === null || item.cost === undefined ? "" : String(item.cost),
      frequency: item.frequency || "One-time",
      requested_by: String(item.requested_by || ""),
      description: item.description ?? "",
      attachments_link: item.attachments_link ?? "",
    });
    setAttachments([]);
    setExistingAttachments(item.attachments ?? []);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Purchase approvals
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleExportCSV}
            className="h-10 px-5"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card ref={formCardRef} className="border-gray-200 bg-white shadow-sm">
        <CardContent className="space-y-5 p-5 md:p-6">
          <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-[#111827]">
                {isEditMode
                  ? "Edit purchase request"
                  : "Raise purchase request"}
              </h2>
              <Info className="h-4 w-4 text-gray-400" />
            </div>
            <div className="flex flex-col gap-3 sm:items-end">
              <div className="flex flex-wrap items-center gap-2">
                {isEditMode ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    disabled={saveMutation.isPending}
                    className="h-10 px-5"
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyToCreate}
                  disabled={saveMutation.isPending}
                  className="h-10 px-5"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy to Create
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saveMutation.isPending}
                  className="bg-primary hover:bg-primary/90 h-10 px-5 text-white"
                >
                  {saveMutation.isPending
                    ? isEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : isEditMode
                      ? "Update"
                      : "Submit for approval"}
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Project <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.project || undefined}
                onValueChange={value =>
                  setForm(prev => ({ ...prev, project: value }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select Project" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {projectOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Request Title"
                value={form.title}
                onChange={e =>
                  setForm(prev => ({ ...prev, title: e.target.value }))
                }
                className="h-10 bg-white"
              />
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Priority <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.priority}
                onValueChange={value =>
                  setForm(prev => ({
                    ...prev,
                    priority: value as PurchaseApprovalPriority,
                  }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select Priority" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {PURCHASE_APPROVAL_PRIORITIES.map(p => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Currency <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.currency}
                onValueChange={value =>
                  setForm(prev => ({
                    ...prev,
                    currency: value as PurchaseApprovalCurrency,
                  }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {PURCHASE_APPROVAL_CURRENCY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Requested Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="date"
                  value={form.requested_date}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      requested_date: e.target.value,
                    }))
                  }
                  className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Expected Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="date"
                  value={form.expected_date}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      expected_date: e.target.value,
                    }))
                  }
                  className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Cost ({currencySymbol(form.currency)}){" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  {currencySymbol(form.currency)}
                </span>
                <Input
                  type="number"
                  placeholder="Cost"
                  value={form.cost}
                  onChange={e =>
                    setForm(prev => ({ ...prev, cost: e.target.value }))
                  }
                  className="h-10 bg-white pl-7"
                />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Frequency <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.frequency}
                onValueChange={value =>
                  setForm(prev => ({ ...prev, frequency: value }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select Frequency" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="One-time">One-time</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Half yearly">Half yearly</SelectItem>
                  <SelectItem value="Yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-4">
              <Label className="text-sm text-[#374151]">
                Requested By <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.requested_by || undefined}
                disabled
                onValueChange={value =>
                  setForm(prev => ({ ...prev, requested_by: value }))
                }
              >
                <SelectTrigger className="h-10 cursor-not-allowed bg-gray-50 text-gray-500 opacity-80">
                  <SelectValue placeholder="Select Requester" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {employeeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex h-full flex-col space-y-1.5 md:col-span-6">
              <Label className="text-sm text-[#374151]">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Provide detailed reason for the purchase request..."
                value={form.description}
                onChange={e =>
                  setForm(prev => ({ ...prev, description: e.target.value }))
                }
                className="min-h-[115px] flex-1 resize-none bg-white"
              />
            </div>

            <div className="space-y-4 md:col-span-6">
              <div className="space-y-1.5">
                <Label className="text-sm text-[#374151]">
                  Attachments Link
                </Label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Paste URL link here"
                    value={form.attachments_link}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        attachments_link: e.target.value,
                      }))
                    }
                    className="h-10 bg-white pr-10"
                  />
                  <Link2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm text-[#374151]">Attachments</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-10 bg-white"
                  >
                    <Paperclip className="mr-2 h-4 w-4 text-gray-400" />
                    Attach Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => onFilesSelected(e.target.files)}
                  />
                  <span className="text-xs text-gray-400">
                    {attachments.length} file(s) selected
                  </span>
                </div>

                {/* Display current files to upload */}
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((file, idx) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="flex items-center gap-1.5 rounded bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                      >
                        <span className="max-w-[120px] truncate">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setAttachments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Display existing attachments (for editing) */}
                {existingAttachments.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-semibold text-gray-500">
                      Existing Attachments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {existingAttachments.map((file, idx) => (
                        <div
                          key={file.document_id ?? idx}
                          className="flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2.5 py-1 text-xs text-blue-600 hover:bg-slate-50"
                        >
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="max-w-[150px] truncate hover:underline"
                          >
                            {file.name ?? "Attachment"}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <PurchaseApprovalFilters
        values={filterValues}
        onChange={setFilterValues}
        idPrefix="ba-purchase-approval"
      />

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-5 py-3 font-medium">PR Number</th>
                  <th className="px-5 py-3 font-medium">Project</th>
                  <th className="px-5 py-3 font-medium">Title</th>
                  <th className="px-5 py-3 font-medium">Priority</th>
                  <th className="px-5 py-3 font-medium">Requested Date</th>
                  <th className="px-5 py-3 font-medium">Expected Date</th>
                  <th className="px-5 py-3 font-medium">Cost</th>
                  <th className="px-5 py-3 font-medium">Frequency</th>
                  <th className="px-5 py-3 font-medium">Requested By</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Purchase Status</th>
                  <th className="w-[88px] px-5 py-3 text-center font-medium">
                    Reason
                  </th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading purchase requests...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No purchase requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map(item => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0 hover:bg-slate-50/50"
                    >
                      <td className="px-5 py-3.5 font-medium text-[#111827]">
                        {item.pr_number || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.project_name ??
                          resolveName(item.project, projectNameById)}
                      </td>
                      <td className="px-5 py-3.5 font-medium text-[#374151]">
                        {item.title || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.priority || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.requested_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.expected_date)}
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-[#374151]">
                        {formatMoney(item.cost, item.currency)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.frequency || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {resolveName(item.requested_by, employeeNameById)}
                      </td>
                      <td className="px-5 py-3.5">
                        <PurchaseApprovalStatusBadge status={item.status} />
                      </td>
                      <td className="px-5 py-3.5">
                        <PurchaseApprovalPurchaseStatusBadge
                          purchaseStatus={item.purchase_status}
                        />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <ReasonCell item={item} />
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(() => {
                            const status = normalizeStatus(item.status);
                            const isLocked =
                              status === "approved" || status === "reject";
                            const isEditable = !isLocked;
                            const isDeletable =
                              !isLocked && status !== "on_hold";

                            return (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setViewId(item.id)}
                                  className="h-8 w-8 text-gray-600 hover:text-[#111827]"
                                  aria-label="View purchase request"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled={!isEditable}
                                  onClick={() => handleEdit(item)}
                                  className={cn(
                                    "text-primary h-8 w-8 hover:text-[#111827c2]",
                                    !isEditable &&
                                      "cursor-not-allowed opacity-40"
                                  )}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Popconfirm
                                  title="Delete Purchase Request"
                                  description="Are you sure you want to delete this purchase request?"
                                  disabled={!isDeletable}
                                  onConfirm={() =>
                                    deleteMutation.mutate(item.id)
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                  okButtonProps={{
                                    loading: deleteMutation.isPending,
                                  }}
                                >
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={!isDeletable}
                                    className={cn(
                                      "h-8 w-8 text-red-600 hover:text-red-400",
                                      !isDeletable &&
                                        "cursor-not-allowed opacity-40"
                                    )}
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </Popconfirm>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["50", "100", "200", "500"]}
                defaultPageSize={100}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <PurchaseApprovalViewModal
        id={viewId}
        open={Boolean(viewId)}
        projectNameById={projectNameById}
        employeeNameById={employeeNameById}
        onOpenChange={open => {
          if (!open) {
            setViewId(null);
          }
        }}
      />
    </div>
  );
};

export default BusinessAnalystTab;
